import { useState } from "react";
import { ChevronDown, ChevronRight, FileSearch, CheckCircle2, XCircle, AlertCircle, MinusCircle } from "lucide-react";

/* ════════════════════════════════════════════════════════════════
   PARSER DIAGNOSTICS (P0 Feb-2026)
   Compact expandable panel rendered below failed / partial files
   in the upload tracker. Shows every parser the orchestrator tried,
   the per-attempt status, the rows it found, and the reason.

   Schema expected from the backend (`parser_diagnostics` field on
   each file result, attached by parse_stage):
     {
       status: "success" | "review_required" | ...,
       selected_parser: "wise_specialist_v1" | null,
       safe_to_continue: boolean,
       rows_extracted: number,
       attempts: [
         { name, status, rows_found, confidence, reason, warnings },
         ...
       ],
       diagnostics: {
         fingerprint, extraction_meta, quality: {score, reasons[]},
         failure_reason,
       }
     }
   ──────────────────────────────────────────────────────────────
   Constraints (locked):
   - Never display generic "Document Intake Failed" — always show
     the full attempt list.
   - Always show whether OCR was attempted (Tesseract availability
     is honest).
   - Never claim the ledger was written.
   ════════════════════════════════════════════════════════════════ */

const ICON_FOR_STATUS = (status) => {
  if (status === "success") return { Icon: CheckCircle2, cls: "text-emerald-400" };
  if (status === "skipped" || status === "n/a" || status === "unavailable") {
    return { Icon: MinusCircle, cls: "text-slate-500" };
  }
  if (status === "tesseract_unavailable") {
    return { Icon: MinusCircle, cls: "text-slate-500" };
  }
  if (status === "no_match" || status === "no_rows" || status === "no_text" || status === "empty") {
    return { Icon: AlertCircle, cls: "text-amber-400" };
  }
  if (status === "error" || status === "failed" || status === "router_error") {
    return { Icon: XCircle, cls: "text-red-400" };
  }
  return { Icon: AlertCircle, cls: "text-slate-400" };
};

const PRETTY_NAME = (name) => ({
  text_extraction:    "Text extraction",
  ocr_extraction:     "OCR",
  specialist_parser:  "Specialist parser",
  column_mapper:      "Column mapper",
  ai_chunk_extractor: "AI chunk extractor",
  legacy_regex_walker:"Legacy regex fallback",
}[name] || name);

export default function ParserDiagnostics({ diagnostics, fileId }) {
  const [open, setOpen] = useState(false);
  if (!diagnostics || !diagnostics.attempts) return null;
  const attempts = diagnostics.attempts || [];
  const failureReason =
    (diagnostics.diagnostics || {}).failure_reason
    || (((diagnostics.diagnostics || {}).quality || {}).reasons || []).join(", ");

  return (
    <div
      className="mt-2 rounded-md border border-slate-800/60 bg-slate-950/40 px-3 py-2"
      data-testid={`parser-diagnostics-${fileId}`}
    >
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-400 hover:text-slate-200 transition-colors"
        data-testid={`parser-diagnostics-toggle-${fileId}`}
      >
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        <FileSearch className="w-3 h-3" />
        Parser cascade
        <span className="text-slate-500 normal-case tracking-normal">
          ({attempts.length} attempts · {diagnostics.rows_extracted} rows)
        </span>
      </button>
      {open && (
        <div className="mt-2 space-y-1" data-testid={`parser-diagnostics-list-${fileId}`}>
          {attempts.map((a, i) => {
            const { Icon, cls } = ICON_FOR_STATUS(a.status);
            return (
              <div
                key={`${a.name}-${i}`}
                className="flex items-start gap-2 text-[10px]"
                data-testid={`parser-attempt-${a.name}-${fileId}`}
                data-attempt-status={a.status}
              >
                <Icon className={`w-3 h-3 mt-0.5 shrink-0 ${cls}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-slate-300">{PRETTY_NAME(a.name)}</span>
                    <span className={`font-mono ${cls}`}>{a.status}</span>
                    {a.rows_found > 0 && (
                      <span className="text-slate-500">
                        {a.rows_found} row{a.rows_found === 1 ? "" : "s"}
                      </span>
                    )}
                    {a.confidence > 0 && (
                      <span className="text-slate-500">
                        conf {Math.round(a.confidence * 100)}%
                      </span>
                    )}
                  </div>
                  {a.reason && (
                    <div className="text-slate-500 mt-0.5 leading-relaxed">
                      {a.reason}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {failureReason && (
            <div
              className="mt-2 pt-2 border-t border-slate-800/60 text-[10px] text-amber-300/80"
              data-testid={`parser-diagnostics-reason-${fileId}`}
            >
              <span className="text-slate-500 uppercase tracking-wider mr-1.5">
                Failure reason:
              </span>
              {failureReason}
            </div>
          )}
          <div className="text-[9px] text-slate-600 mt-1 italic">
            No ledger entries were created.
          </div>
        </div>
      )}
    </div>
  );
}
